import * as React from "react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

type Meridiem = "AM" | "PM";

interface FeedTimePickerProps {
  /** "HH:MM" 24-hour, or "" when unset. */
  value: string;
  onChange: (value: string) => void;
  id?: string;
  disabled?: boolean;
}

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);

const parse = (value: string) => {
  if (!value) return { hh: "", mm: "", meridiem: "AM" as Meridiem };
  const [h, m] = value.split(":");
  const hourNum = parseInt(h, 10);
  const minNum = parseInt(m ?? "", 10);
  if (Number.isNaN(hourNum)) {
    return { hh: "", mm: "", meridiem: "AM" as Meridiem };
  }
  const meridiem: Meridiem = hourNum >= 12 ? "PM" : "AM";
  const hour12 = hourNum % 12 === 0 ? 12 : hourNum % 12;
  return {
    hh: String(hour12),
    mm: Number.isNaN(minNum) ? "" : String(minNum),
    meridiem,
  };
};

const compose = (hh: string, mm: string, meridiem: Meridiem) => {
  const hourNum = parseInt(hh, 10);
  const minNum = parseInt(mm, 10);
  if (Number.isNaN(hourNum) || Number.isNaN(minNum)) return "";
  if (hourNum < 1 || hourNum > 12 || minNum < 0 || minNum > 59) return "";
  const hour24 = meridiem === "PM" ? (hourNum % 12) + 12 : hourNum % 12;
  return `${String(hour24).padStart(2, "0")}:${String(minNum).padStart(2, "0")}`;
};

export const FeedTimePicker: React.FC<FeedTimePickerProps> = ({
  value,
  onChange,
  id,
  disabled,
}) => {
  const parsed = React.useMemo(() => parse(value), [value]);
  const [hh, setHH] = React.useState(parsed.hh);
  const [mm, setMM] = React.useState(parsed.mm);
  const [meridiem, setMeridiem] = React.useState<Meridiem>(parsed.meridiem);

  // Sync from parent only when parent has a real value that differs from local
  // state. We hold partial selections (e.g. HH chosen, MM not) locally so the
  // Select trigger keeps showing the user's pick before compose() can emit.
  React.useEffect(() => {
    if (!value) return;
    if (parsed.hh !== hh || parsed.mm !== mm || parsed.meridiem !== meridiem) {
      setHH(parsed.hh);
      setMM(parsed.mm);
      setMeridiem(parsed.meridiem);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (nextHH: string, nextMM: string, nextMeridiem: Meridiem) => {
    setHH(nextHH);
    setMM(nextMM);
    setMeridiem(nextMeridiem);
    onChange(compose(nextHH, nextMM, nextMeridiem));
  };

  return (
    <div className="flex items-center gap-2">
      <Select
        value={hh}
        onValueChange={(next) => emit(next, mm, meridiem)}
        disabled={disabled}
      >
        <SelectTrigger id={id} className="w-20 tabular-nums">
          <SelectValue placeholder="HH" />
        </SelectTrigger>
        <SelectContent>
          {HOURS.map((h) => (
            <SelectItem key={h} value={String(h)} className="tabular-nums">
              {String(h).padStart(2, "0")}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span aria-hidden className="text-muted-foreground select-none">
        :
      </span>
      <Select
        value={mm}
        onValueChange={(next) => emit(hh, next, meridiem)}
        disabled={disabled}
      >
        <SelectTrigger className="w-20 tabular-nums">
          <SelectValue placeholder="MM" />
        </SelectTrigger>
        <SelectContent>
          {MINUTES.map((m) => (
            <SelectItem key={m} value={String(m)} className="tabular-nums">
              {String(m).padStart(2, "0")}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <ToggleGroup
        type="single"
        spacing={2}
        variant="outline"
        size="sm"
        value={meridiem}
        onValueChange={(next) => {
          if (next) emit(hh, mm, next as Meridiem);
        }}
        disabled={disabled}
      >
        <ToggleGroupItem value="AM" aria-label="AM">
          AM
        </ToggleGroupItem>
        <ToggleGroupItem value="PM" aria-label="PM">
          PM
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  );
};
